'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Solicitud } from '@/types/solicitud';
import { cobranzaService } from '@/lib/api/cobranzaService';
import { Button } from '@/components/ui/Button';
import { SolicitudTable } from './SolicitudTable';
import { SolicitudDetail } from './SolicitudDetail';
import { useAuth } from '@/hooks/useAuth';
import ErrorMessage from '@/components/common/ErrorMessage';

const CobranzaManagement: React.FC = () => {
  const { user } = useAuth();
  const [solicitudes, setSolicitudes] = useState<Solicitud[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtroCriterio, setFiltroCriterio] = useState('');
  const [filtroValor, setFiltroValor] = useState('');
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState<Solicitud | null>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  // Campos filtrables para cobranza
  const camposFiltrables = [
    { value: 'solicitudNumeroControl', label: 'Número de Control' },
    { value: 'clienteNombre', label: 'Nombre del Cliente' },
    { value: 'clienteEmpresa', label: 'Empresa' },
    { value: 'asesorNombre', label: 'Asesor' },
    { value: 'producto', label: 'Producto' },
    { value: 'clienteReferenciaCobranza', label: 'Referencia Cobranza' },
  ];

  // Solo cargar datos inicialmente, no cuando cambian los filtros
  useEffect(() => {
    const ownerID = (user as any)?.sub || (user as any)?.ownerID || '';
    if (ownerID) {
      loadSolicitudes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sub]); // Solo cuando cambia el usuario

  const loadSolicitudes = useCallback(async (criterioOverride?: string, valorOverride?: string) => {
    const ownerID = (user as any)?.sub || (user as any)?.ownerID || '';
    if (!ownerID) {
      setIsLoading(false);
      return;
    }

    const criterio = criterioOverride !== undefined ? criterioOverride : filtroCriterio;
    const valor = valorOverride !== undefined ? valorOverride : filtroValor;

    setIsLoading(true);
    setError(null);

    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Tiempo de espera agotado')), 15000)
      );

      const res = await Promise.race([
        cobranzaService.getSolicitudes(
          ownerID,
          criterio || undefined,
          valor || undefined
        ),
        timeoutPromise
      ]) as any;

      if (res.data) {
        setSolicitudes(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res.error || 'Error al cargar solicitudes');
        setSolicitudes([]);
      }
    } catch (err: any) {
      console.error('Error cargando solicitudes Cobranza:', err);
      setError(err.message || 'Error al cargar solicitudes');
      setSolicitudes([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.sub, filtroCriterio, filtroValor]);

  const handleBuscar = () => {
    loadSolicitudes();
  };

  const handleLimpiarFiltros = () => {
    setFiltroCriterio('');
    setFiltroValor('');
    loadSolicitudes('', '');
  };

  const handleGenerarReporte = async () => {
    const ownerID = (user as any)?.sub || (user as any)?.ownerID || '';
    if (!ownerID) return;
    
    setIsGeneratingReport(true);
    setError(null);
    
    try {
      await cobranzaService.getReporteCobranzaReferenciada(ownerID);
    } catch (err: any) {
      setError(err.message || 'Error al generar reporte');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleUpdateSolicitud = useCallback((updated: Solicitud) => {
    setSolicitudes(prev => prev.map(s => s.id === updated.id ? updated : s));
    if (selectedSolicitud?.id === updated.id) {
      setSelectedSolicitud(updated);
    }
  }, [selectedSolicitud]);

  const handleEdit = (solicitud: Solicitud) => {
    setSelectedSolicitud(solicitud);
    setIsDetailOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cobranza</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestión de solicitudes fondeadas para seguimiento de cobranza
          </p>
        </div>
        <Button 
          onClick={handleGenerarReporte}
          disabled={isGeneratingReport}
          variant="primary"
        >
          {isGeneratingReport ? 'Generando...' : 'Cobranza Referenciada'}
        </Button>
      </div>

      {error && <ErrorMessage message={error} />}

      {/* Filtros */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Criterio</label>
            <select
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={filtroCriterio}
              onChange={(e) => setFiltroCriterio(e.target.value)}
            >
              <option value="">Seleccionar criterio</option>
              {camposFiltrables.map(campo => (
                <option key={campo.value} value={campo.value}>
                  {campo.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
            <input
              type="text"
              value={filtroValor}
              onChange={(e) => setFiltroValor(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleBuscar();
                }
              }}
              placeholder="Ingrese valor a buscar..."
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="primary" onClick={handleBuscar}>
              Buscar
            </Button>
            <Button variant="secondary" onClick={handleLimpiarFiltros}>
              Limpiar
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600" />
        </div>
      ) : solicitudes.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
          <div className="text-sm font-semibold text-gray-900">Sin solicitudes</div>
          <div className="mt-1 text-sm text-gray-500">
            {filtroCriterio && filtroValor 
              ? 'No hay solicitudes fondeadas que coincidan con los filtros.'
              : 'No hay solicitudes fondeadas disponibles.'}
          </div>
        </div>
      ) : (
        <>
          <SolicitudTable
            solicitudes={solicitudes}
            onEdit={handleEdit}
            montoColumn="autorizado"
          />
          <div className="text-right text-xs text-gray-400 px-2">
            Total: {solicitudes.length} solicitudes
          </div>
        </>
      )}

      {/* Detail Modal */}
      <SolicitudDetail
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        solicitud={selectedSolicitud}
        onUpdate={handleUpdateSolicitud}
        modulo="cobranza"
      />
    </div>
  );
};

export default CobranzaManagement;
